import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { useI18n } from '../hooks/useI18n'
import GuestBanner from '../components/GuestBanner'
import LoadingState from '../components/LoadingState'
import Button from '../components/Button'

/**
 * The Six — VIP, port of wechat/src/pages/six/index.jsx.
 *
 * Two states, and the page previously had NEITHER: it rendered three keys that
 * do not exist in either dictionary (`six.kicker`, `six.title`, `six.empty`),
 * so members saw the literal strings "six.kicker / six.title / six.empty".
 * `createTranslator` falls back to the key when a lookup misses, which is the
 * right behaviour for a missing translation and a terrible one for an invented
 * key — nothing warns you, it just prints the identifier.
 *
 *   INVITED  — the private invitation, when a vip match exists.
 *   OTHERWISE — the explainer: what The Six is and how hand-matching works.
 *
 * The explainer is not a placeholder for an empty list. It is the page for
 * everyone who has not been invited yet, which is nearly everyone.
 */
export default function SixPage({
  registered, onRegister, onSeeDining
}: { registered: boolean; onRegister: () => void; onSeeDining: () => void }) {
  const { t } = useI18n()
  const [matches, setMatches] = useState<Array<{ status?: string; expires_in?: string }> | null>(null)

  useEffect(() => {
    if (!registered) return
    // VIP only: a social booking is not an invitation to The Six.
    api.getMyEventMatches('vip')
      .then((result) => setMatches(Array.isArray(result) ? result : []))
      .catch(() => setMatches([]))
  }, [registered])

  // Matching cannot say anything useful about someone who has not answered a
  // single question, so the endpoint is not even called for a guest.
  if (!registered) {
    return <div><GuestBanner variant="six" onRegister={onRegister} /></div>
  }
  if (matches === null) return <LoadingState />

  const invited = matches.find((m) => ['invited', 'accepted', 'booked'].includes(String(m.status)))

  return (
    <div>
      <div className="text-center">
        <span className="inline-block rounded-pill border border-gold-dim px-3 py-1 text-2xs uppercase tracking-kicker text-gold">
          {t('six.badge')}
        </span>
      </div>

      {invited ? (
        <>
          <h1 className="mt-3.5 font-display text-hero italic leading-tight text-ivory">{t('six.inTitle')}</h1>
          <p className="mt-3 text-13 leading-relaxed text-muted">{t('six.inSub')}</p>

          {/* The full InvitationCard is a mini-program component with its own
              accept/pay flow. Until that is ported, showing the invitation
              honestly and sending people to Social Dining beats rendering a
              half-working Accept button that takes money nowhere. */}
          <div className="mt-3.5 rounded-md border border-gold-dim bg-card px-[18px] py-4">
            <p className="text-md text-ivory">{t('six.inWhen')}</p>
            <p className="mt-1 text-13 text-muted">{t('six.inSeats')}</p>
          </div>

          <div className="mt-3.5">
            <Button variant="ghost" onClick={onSeeDining}>{t('six.inLater')}</Button>
          </div>
          {invited.expires_in ? (
            <p className="mt-3 text-center text-sm text-muted">
              {t('six.inExpires')} {invited.expires_in}
            </p>
          ) : null}
        </>
      ) : (
        <>
          <h1 className="mt-3.5 font-display text-hero italic leading-tight text-ivory">{t('six.exTitle')}</h1>
          <p className="mt-3 text-13 leading-relaxed text-muted">{t('six.exSub')}</p>

          <p className="mt-5 text-2xs uppercase tracking-kicker text-gold">{t('six.exHow')}</p>
          <div className="mt-2 rounded-md border border-gold-dim bg-card px-[18px]">
            <Step n="1" title={t('six.s1t')} body={t('six.s1p')} />
            <Step n="2" title={t('six.s2t')} body={t('six.s2p')} />
            <Step n="3" title={t('six.s3t')} body={t('six.s3p')} last />
          </div>

          <p className="mt-3.5 text-xs leading-relaxed text-muted-2">{t('six.exFoot')}</p>
          <div className="mt-3.5">
            <Button onClick={onSeeDining}>{t('six.exCta')}</Button>
          </div>
        </>
      )}
    </div>
  )
}

function Step({ n, title, body, last = false }: { n: string; title: string; body: string; last?: boolean }) {
  return (
    <div className={`flex gap-3 py-4 ${last ? '' : 'border-b border-gold-dim'}`}>
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gold-dim text-2xs text-gold">
        {n}
      </span>
      <div className="min-w-0">
        <p className="text-md text-ivory">{title}</p>
        <p className="mt-1 text-13 leading-relaxed text-muted">{body}</p>
      </div>
    </div>
  )
}
